import { Typography, useMobile } from "@cpj-ai/panel-npm-package";
import { Button, CircularProgress, Modal, ModalBody, ModalContent, ModalHeader } from "@heroui/react";
import { useState } from "react";

interface AssessmentDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenChange: (open: boolean) => void;
  assessmentDetail?: {
    id: string;
    title: string;
    description: string;
    status: string;
    remainingQuestions: number;
    totalEstimatedTime: number;
    totalQuestions: number;
    skill?: {
      title: string;
      description: string;
    } | null;
    results?: Array<{
      evaluation?: {
        score: string;
        userFeedback: string;
        negativePoints: Array<string | null>;
        positivePoints: Array<string | null>;
        scoreReasoning: string;
        scoreValue?: unknown;
      } | null;
      question: {
        text: string;
      };
    } | null> | null;
  } | null;
}

export const AssessmentDetailModal: React.FC<AssessmentDetailModalProps> = ({
  isOpen,
  onClose,
  onOpenChange,
  assessmentDetail,
}) => {
  const [selectedTab, setSelectedTab] = useState("overview");
  const isMobile = useMobile();

  // Helper function to convert score value to percentage
  const convertScoreToPercentage = (scoreValue: string | undefined): number => {
    if (!scoreValue) return 0;
    const numericValue = parseFloat(scoreValue);
    return Math.round(numericValue * 100); // Convert 0-1 range to 0-100
  };

  // Helper function to get numeric score from evaluation
  const getNumericScore = (
    evaluation:
      | {
          score?: string;
          scoreValue?: unknown;
        }
      | null
      | undefined,
  ): number => {
    // Prioritize scoreValue if available (this is the actual numeric score)
    if (evaluation?.scoreValue && typeof evaluation.scoreValue === "string") {
      return convertScoreToPercentage(evaluation.scoreValue);
    }
    // Fallback: convert score string to numeric value
    // Using the same mapping as assessmentScoring.ts utility
    const scoreMap: { [key: string]: number } = {
      EXCEPTIONAL: 95,
      GREAT: 85,
      GOOD: 75,
      AVERAGE: 60,
      POOR: 30,
      ZERO: 0,
    };
    return scoreMap[evaluation?.score || ""] || 0;
  };

  // Calculate statistics
  const results = assessmentDetail?.results?.filter((result) => result !== null) || [];
  const completedResults = results.filter((result) => result?.evaluation?.score);
  const correctAnswers = completedResults.filter((result) => {
    const score = getNumericScore(result?.evaluation);
    return score >= 70; // Consider 70+ as correct
  }).length;

  // Calculate overall score
  const totalScore = completedResults.reduce((sum, result) => {
    return sum + getNumericScore(result?.evaluation);
  }, 0);
  const averageScore = completedResults.length > 0 ? totalScore / completedResults.length : 0;
  const isPassed = averageScore >= 70;

  const getGradeEmoji = () => {
    if (averageScore >= 90) return "🏆";
    if (averageScore >= 80) return "🎉";
    if (averageScore >= 70) return "👍";
    return "📚";
  };

  const getStatusText = () => {
    return isPassed ? "Passed" : "Failed";
  };

  const getStatusTextColor = () => {
    return isPassed ? "text-green-600" : "text-red-600";
  };

  const getCircularProgressColor = () => {
    if (averageScore >= 80) return "success";
    if (averageScore >= 70) return "warning";
    return "danger";
  };

  if (!assessmentDetail) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onOpenChange={onOpenChange}
      size={isMobile ? "full" : "2xl"}
      scrollBehavior="inside"
      classNames={{
        base: "sm:max-w-2xl sm:max-h-[90vh] max-h-screen",
        body: "px-4 py-2 sm:px-6 sm:py-4",
        header: "px-4 pt-4 pb-2 sm:px-6 sm:pt-6 sm:pb-4",
      }}
    >
      <ModalContent>
        <ModalHeader className="flex flex-col gap-0 pb-2">
          <div className="flex justify-between items-start mb-3">
            <div className="flex items-center gap-3 flex-1">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center">
                <span className="text-lg">{getGradeEmoji()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <Typography className="font-bold text-gray-900 line-clamp-1 text-xl">Assessment Results</Typography>
                <Typography variant="small" className="text-gray-600 line-clamp-1">
                  {assessmentDetail.title}
                </Typography>
              </div>
            </div>
          </div>

          {/* Score Hero Section */}
          <div className="flex flex-col items-center p-4 sm:p-6 bg-gradient-to-br from-purple-50/50 to-blue-50/50 rounded-2xl mb-4">
            <div className="flex items-center justify-between w-full">
              <CircularProgress
                aria-label="Assessment Score"
                size="lg"
                value={Math.round(averageScore)}
                color={getCircularProgressColor()}
                showValueLabel
                classNames={{
                  svg: "w-32 h-32 drop-shadow-lg",
                  track: "stroke-black/10",
                  value: "text-3xl font-bold text-gray-800",
                }}
              />
              <div className="text-center mt-4">
                <Typography className={`font-bold text-2xl ${getStatusTextColor()}`}>{getStatusText()}</Typography>
                <Typography variant="small" className="text-gray-600">
                  {Math.round(averageScore)} / 100 Points
                </Typography>
              </div>
            </div>
            <div className="w-full border-t border-gray-200/80 my-4 sm:my-6" />
            <div className="flex justify-between w-full text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <span>⏰</span>
                <span>{Math.round((assessmentDetail?.totalEstimatedTime || 0) / 60) || 0} min duration</span>
              </div>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {/* Tab Navigation */}
          <div className="flex border-b border-gray-200 mb-4">
            <button
              onClick={() => setSelectedTab("overview")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                selectedTab === "overview"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className={selectedTab === "overview" ? "text-primary-500" : "text-gray-500"}>📊</span>
              <span>Overview</span>
            </button>
            <button
              onClick={() => setSelectedTab("questions")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                selectedTab === "questions"
                  ? "border-primary-500 text-primary-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <span className={selectedTab === "questions" ? "text-primary-500" : "text-gray-500"}>💬</span>
              <span>Questions</span>
            </button>
          </div>

          {/* Tab Content */}
          {selectedTab === "overview" && (
            <div className="space-y-6 pt-4">
              {/* Statistics Section */}
              <div>
                <Typography className="font-semibold text-gray-800 mb-4 text-xl">Assessment Summary</Typography>
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {/* Correct Answers Card */}
                  <div className="bg-green-50 p-2 sm:p-5 rounded-xl shadow-sm border border-green-500 hover:shadow-md transition-all duration-200 group">
                    <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-green-100 rounded-full flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <span className="text-green-600">✅</span>
                      </div>
                      <div>
                        <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">{correctAnswers}</div>
                        <Typography variant="small" className="text-gray-600 font-medium text-xs sm:text-sm">
                          Correct
                        </Typography>
                      </div>
                    </div>
                  </div>

                  {/* Incorrect Answers Card */}
                  <div className="bg-red-50 p-2 sm:p-5 rounded-xl shadow-sm border border-red-500 hover:shadow-md transition-all duration-200 group">
                    <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center group-hover:bg-red-200 transition-colors">
                        <span className="text-red-600">❌</span>
                      </div>
                      <div>
                        <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">
                          {completedResults.length - correctAnswers}
                        </div>
                        <Typography variant="small" className="text-gray-600 font-medium text-xs sm:text-sm">
                          Incorrect
                        </Typography>
                      </div>
                    </div>
                  </div>

                  {/* Total Questions Card */}
                  <div className="bg-blue-50 p-2 sm:p-5 rounded-xl shadow-sm border border-blue-500 hover:shadow-md transition-all duration-200 group">
                    <div className="flex flex-col items-center text-center space-y-2 sm:space-y-3">
                      <div className="w-10 h-10 sm:w-14 sm:h-14 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                        <span className="text-blue-600">📋</span>
                      </div>
                      <div>
                        <div className="text-xl sm:text-3xl font-bold text-gray-900 mb-1">{results.length}</div>
                        <Typography variant="small" className="text-gray-600 font-medium text-xs sm:text-sm">
                          Total
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Performance Insight Section */}
              <div>
                <Typography className="font-semibold text-gray-800 mb-3 text-xl">Key Takeaway</Typography>
                <div className="bg-gradient-to-r from-primary-50 to-blue-50 p-4 rounded-xl border border-primary-200">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-white/50 rounded-lg flex items-center justify-center shrink-0 mt-1">
                      <span className="text-primary-600">ℹ️</span>
                    </div>
                    <div>
                      <Typography variant="small" className="text-primary-700 leading-relaxed">
                        {isPassed
                          ? `Excellent work! You scored ${Math.round(averageScore)}% and exceeded the 70% threshold. Your understanding of the key concepts is strong.`
                          : `You scored ${Math.round(averageScore)}%, which is below the 70% threshold. Review the feedback below to improve your understanding.`}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>

              {/* Assessment Details */}
              <div>
                <Typography className="font-semibold text-gray-800 mb-3 text-xl">Assessment Details</Typography>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Completed on:</span>
                      <span className="ml-2 font-medium text-gray-900">{new Date().toLocaleDateString()}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Duration:</span>
                      <span className="ml-2 font-medium text-gray-900">
                        {Math.round((assessmentDetail?.totalEstimatedTime || 0) / 60) || 0} minutes
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Questions:</span>
                      <span className="ml-2 font-medium text-gray-900">{assessmentDetail.totalQuestions}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Status:</span>
                      <span className={`ml-2 font-medium ${getStatusTextColor()}`}>{getStatusText()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {selectedTab === "questions" && (
            <div className="space-y-3 pt-4">
              {results.map((result, index) => {
                const score = getNumericScore(result?.evaluation);
                const isCorrect = score >= 70;

                return (
                  <div
                    key={result?.question?.text ? result.question.text.slice(0, 50) : `question-${Math.random()}`}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      isCorrect ? "border-green-200 bg-green-50" : "border-red-200 bg-red-50"
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-8 h-8 min-w-8 min-h-8 rounded-full flex items-center justify-center text-white font-bold text-sm ${
                              isCorrect ? "bg-green-500" : "bg-red-500"
                            }`}
                          >
                            {index + 1}
                          </div>
                          <Typography variant="small" className="font-medium text-gray-900 leading-relaxed">
                            {result?.question?.text || `Question ${index + 1}`}
                          </Typography>
                        </div>

                        <div className="flex items-center gap-2 mt-2 justify-end">
                          {isCorrect ? (
                            <span className="text-green-500">✅</span>
                          ) : (
                            <span className="text-red-500">❌</span>
                          )}
                          <span className="text-xs sm:text-sm font-semibold text-gray-700">{score}/100 pts</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      {/* Score Reasoning */}
                      {result?.evaluation?.scoreReasoning && (
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <Typography variant="small" className="text-blue-800 mb-1 font-medium">
                            Score Analysis:
                          </Typography>
                          <Typography variant="small" className="text-blue-900 leading-relaxed">
                            {result.evaluation.scoreReasoning}
                          </Typography>
                        </div>
                      )}

                      {/* User Feedback */}
                      {result?.evaluation?.userFeedback && (
                        <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <Typography variant="small" className="text-blue-800 mb-1 font-medium">
                            Feedback:
                          </Typography>
                          <Typography variant="small" className="text-blue-900 leading-relaxed">
                            {result.evaluation.userFeedback}
                          </Typography>
                        </div>
                      )}

                      {/* Positive Points */}
                      {result?.evaluation?.positivePoints && result.evaluation.positivePoints.length > 0 && (
                        <div className="bg-green-50 p-3 rounded-lg border border-green-200">
                          <Typography variant="small" className="text-green-800 mb-1 font-medium">
                            Strengths:
                          </Typography>
                          <ul className="space-y-1">
                            {result.evaluation.positivePoints
                              .filter((point) => point)
                              .map((point, pointIndex) => (
                                <li key={`positive-${point}-${pointIndex}`} className="flex items-start gap-2">
                                  <span className="text-green-500 text-xs mt-1 flex-shrink-0">✓</span>
                                  <Typography variant="small" className="text-green-700">
                                    {point}
                                  </Typography>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}

                      {/* Negative Points */}
                      {result?.evaluation?.negativePoints && result.evaluation.negativePoints.length > 0 && (
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                          <Typography variant="small" className="text-orange-800 mb-1 font-medium">
                            Areas for Improvement:
                          </Typography>
                          <ul className="space-y-1">
                            {result.evaluation.negativePoints
                              .filter((point) => point)
                              .map((point, pointIndex) => (
                                <li key={`negative-${point}-${pointIndex}`} className="flex items-start gap-2">
                                  <span className="text-orange-500 text-xs mt-1 flex-shrink-0">⚠</span>
                                  <Typography variant="small" className="text-orange-700">
                                    {point}
                                  </Typography>
                                </li>
                              ))}
                          </ul>
                        </div>
                      )}

                      {!isCorrect && (
                        <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                          <Typography variant="small" className="text-orange-800 mb-1 font-medium">
                            Score: {score}/100
                          </Typography>
                          <Typography variant="small" className="text-orange-900 leading-relaxed">
                            This question received a {score}% score. Consider reviewing this topic for improvement.
                          </Typography>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {results.length === 0 && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="p-4 bg-gray-100 rounded-full mb-4">
                    <span className="text-gray-400 text-3xl">📊</span>
                  </div>
                  <Typography className="text-lg font-semibold text-gray-700 mb-2">No Results Available Yet</Typography>
                  <Typography className="text-gray-500 max-w-md">
                    Complete the assessment to see detailed results, scores, and feedback for each question.
                  </Typography>
                </div>
              )}
            </div>
          )}
        </ModalBody>

        {/* Action Buttons */}
        <div className="px-4 pb-4 sm:px-6 sm:pb-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              color="primary"
              variant="solid"
              onPress={onClose}
              className="w-full sm:flex-1 py-3 rounded-xl font-semibold"
              size="lg"
            >
              Close
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default AssessmentDetailModal;
